import { css } from "lit";

export const styles = css`
  :host {
    display: contents;

    overflow: hidden;

    /* Animation duration as unitless seconds for JavaScript access */
    --modal-animation-duration-s: 0.3;

    /* Animation duration as CSS time unit for transitions */
    --modal-animation-duration: calc(var(--modal-animation-duration-s) * 1s);
  }

  :host([data-closing]) .modal-overlay {
    opacity: 0;
    transform: scale(0.8);
  }

  :host([data-closing]) .modal-overlay::backdrop {
    background: transparent;
    backdrop-filter: none;
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 1;
    transition:
      opacity var(--modal-animation-duration) ease-out,
      transform var(--modal-animation-duration) ease-out,
      display var(--modal-animation-duration) allow-discrete,
      overlay var(--modal-animation-duration) allow-discrete;
  }

  .modal-overlay:not(:popover-open) {
    opacity: 0;
  }

  .modal-overlay::backdrop {
    background: var(--sds-color-background-utilities-overlay);
    backdrop-filter: blur(var(--sds-size-blur-100));
    pointer-events: all;
    transition:
      background var(--modal-animation-duration) ease-out,
      backdrop-filter var(--modal-animation-duration) ease-out,
      display var(--modal-animation-duration) allow-discrete,
      overlay var(--modal-animation-duration) allow-discrete;
  }

  .modal-overlay:not(:popover-open)::backdrop {
    background: transparent;
    backdrop-filter: none;
  }

  @starting-style {
    .modal-overlay:popover-open {
      opacity: 0;
      transform: scale(0.8);
    }

    .modal-overlay:popover-open::backdrop {
      backdrop-filter: none;
    }
  }

  .popover {
    margin: 0;
    padding: 0;
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    background: var(--sds-color-background-default-default);
    border-radius: var(--sds-size-radius-600);
    box-shadow:
      0 var(--sds-size-depth-400) var(--sds-size-depth-800) var(--sds-size-depth-negative-100)
        var(--sds-color-black-200),
      0 var(--sds-size-depth-200) var(--sds-size-depth-200) var(--sds-size-depth-negative-100)
        var(--sds-color-black-100);
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  :host([alternative-background]) .popover {
    background: var(--sds-color-background-default-secondary);
  }

  .modal {
    position: relative;
    background: var(--sds-color-background-default-tertiary);
    border-radius: var(--sds-size-radius-600);
    box-shadow:
      0 20px 25px -5px rgb(0 0 0 / 0.1),
      0 10px 10px -5px rgb(0 0 0 / 0.04);
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition:
      opacity var(--modal-animation-duration) ease-out,
      transform var(--modal-animation-duration) ease-out;
  }

  .modal-header {
    position: relative;
    padding: var(--sds-size-space-600);
    border-bottom: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    display: grid;
    grid-template-columns: 40px 1fr 40px;
    align-items: center;
    flex-shrink: 0;
  }

  .modal-content {
    padding: var(--sds-size-space-800);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .close-button {
    background: none;
    border: none;
    color: var(--sds-color-icon-default-secondary);
    cursor: pointer;
    padding: var(--sds-size-space-200);
    border-radius: var(--sds-size-radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    transition:
      color 0.2s ease-out,
      background-color 0.2s ease-out;
  }

  :host([alternative-close-button]) .close-button {
    transform: rotate(135deg);
    width: 27px;
    height: 27px;
    color: var(--sds-color-icon-default-default);
  }

  .close-button:hover {
    color: var(--sds-color-icon-default-default);
    background-color: var(--sds-color-background-default-secondary);
  }

  .close-button:focus-visible {
    outline: var(--sds-size-stroke-focus-ring) solid var(--sds-color-border-brand-default);
    outline-offset: var(--sds-size-space-100);
  }

  .back-button {
    background: none;
    border: none;
    color: var(--sds-color-icon-default-default);
    cursor: pointer;
    padding: 0;
    display: none;
    align-items: center;
    justify-content: center;
  }

  .back-button:focus-visible {
    outline: var(--sds-size-stroke-focus-ring) solid var(--sds-color-border-brand-default);
    outline-offset: var(--sds-size-space-100);
  }

  ::slotted([slot="one-column"]) {
    overflow: hidden;
  }

  .body-columns {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* Desktop styles (≥768px) */
  @media (min-width: 768px) {
    .popover {
      min-width: 749px;
      max-width: 90vw;
      max-height: 90vh;
      width: auto;
      height: auto;
      margin: 0;
      position: relative;
      place-self: center;
    }

    @starting-style {
      .modal-overlay:popover-open {
        opacity: 0;
        transform: scale(0.8);
      }
    }

    .desktop-close {
      display: flex;
    }

    .back-button {
      display: none;
    }

    .modal-overlay:not(:popover-open) {
      transform: scale(0.8);
    }
  }

  /* Mobile styles (<768px) */
  @media (max-width: 767px) {
    .modal-overlay {
      align-items: flex-end; /* slide from bottom */
      justify-content: center;
      overflow: hidden;
      transition:
        transform var(--modal-animation-duration) ease-out,
        opacity var(--modal-animation-duration) ease-out,
        display var(--modal-animation-duration) allow-discrete,
        overlay var(--modal-animation-duration) allow-discrete;
    }

    .popover {
      width: 100vw;
      height: auto;
      max-width: 100vw;
      max-height: 100dvh;
      border-radius: var(--sds-size-radius-900) var(--sds-size-radius-900) 0 0;
      margin: 0;
      transform: translateY(100%);
      transition: transform var(--modal-animation-duration) ease-out;
    }

    .popover.fullheight {
      border-radius: 0;
    }

    :host([mobile-fullscreen]) .popover.fullheight {
      height: 100dvh;
    }

    @starting-style {
      .modal-overlay:popover-open {
        opacity: 0;
        transform: translateY(100%);
      }
    }

    .modal-overlay:popover-open .popover {
      transform: translateY(0);
    }

    .mobile-close {
      display: flex;
    }

    :host([alternative-close-button]) .mobile-close {
      transform: rotate(0deg);
    }

    :host([mobile-back-arrow]) .back-button.mobile-back {
      display: flex;
    }

    :host([mobile-back-arrow]) .close-button.desktop-close {
      display: none;
    }

    :host([mobile-back-arrow]) .modal-header {
      grid-template-columns: 40px 1fr 40px;
    }

    :host([mobile-back-arrow]) .header-title {
      text-align: left;
      justify-self: start;
    }

    .modal-body {
      overflow: auto;
      padding: var(--sds-size-space-800);
    }

    .modal-footer {
      padding: var(--sds-size-space-600);
      justify-content: stretch;
    }

    .modal-footer wow-button {
      --wow-button-display: block;
      --wow-button-width: 100%;
      --wow-button-inner-display: flex;
      --wow-button-inner-width: 100%;
    }

    .modal-overlay:not(:popover-open) {
      transform: translateY(100%);
    }

    :host([data-closing]) .modal-overlay {
      transform: translateY(100%);
      opacity: 1;
    }
  }

  /* Variant overrides */
  .modal.overlay {
    /* Force overlay behavior regardless of screen size */
    min-width: 749px;
    min-height: 466px;
    max-width: 90vw;
    max-height: 90vh;
    width: auto;
    height: auto;
    border-radius: var(--sds-size-radius-600);
  }

  .modal.overlay .mobile-close {
    display: none;
  }

  .modal.overlay .desktop-close {
    display: flex;
  }

  .modal.fullscreen {
    /* Force fullscreen behavior regardless of screen size */
    width: 100%;
    height: 100%;
    border-radius: 0;
    max-height: 100dvh;
  }

  .modal.fullscreen .mobile-close {
    display: flex;
  }

  .modal.fullscreen .desktop-close {
    display: none;
  }

  .modal-content:empty {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sds-color-text-default-secondary);
    font-family: var(--sds-typography-family-body);
    font-size: var(--sds-typography-scale-02);
  }

  .modal-content:empty::before {
    content: "Content will be added here";
  }

  /* New modal structure styles */
  .header-title {
    text-align: center;
    font-family: var(--sds-typography-family-title);
    font-weight: var(--sds-typography-weight-medium);
    font-size: var(--sds-typography-scale-04);
    line-height: 1.5;
    color: var(--sds-color-text-default-default);
    grid-column: 2;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: var(--sds-size-space-800);
    flex: 1;
  }

  :host([hide-top-padding]) .modal-body,
  :host([hide-top-padding]) .body-columns {
    padding-top: 0;
    overflow: auto;
  }

  .modal-footer {
    padding: var(--sds-size-space-800);
    border-top: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
  }

  /* Styles for prevent-close functionality */
  :host([prevent-close]) .modal-body {
    /* When footer is hidden, modal body can expand to full available space */
    flex: 1;
  }

  :host([prevent-close]) .modal-header {
    /* Ensure proper spacing when close buttons are hidden */
    grid-template-columns: 0 1fr 0;
  }

  :host([prevent-close]) .header-title {
    /* Center title when no close buttons are present */
    margin: 0;
  }
`;
